// Intake-logs admin — manage who has logs access, point the sync at the right
// CloudWatch log groups, watch sync health, and review the raw-access audit
// log. Admin-only: the page self-gates on logsRole === 'admin', and every
// underlying operation is authorized server-side too (callables check the
// caller's role; config writes and audit reads are gated by rules).

import { useCallback, useEffect, useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useHistory } from 'react-router-dom';

import { getCurrentUser, readUser } from '../firebase';
import {
  listRecentLogsAudit,
  listSyncState,
  loadLogGroupsConfig,
  saveLogGroupsConfig,
  setLogsRole,
  syncIntakeLogsNow,
} from '../intake-logs/firebase';
import { LogGroupConfig, LogsAccessAudit, LogsRole, LogsSyncState, resolveLogsRole } from '../intake-logs/types';
import { fmtWhen, LOGS_ROLE_LABELS, LOGS_ROLE_ORDER } from '../intake-logs/constants';

import '../pm/theme.css';

type Access = { kind: 'loading' } | { kind: 'denied' } | { kind: 'ok' };

const LogsAdmin: React.FC = () => {
  const history = useHistory();
  const [access, setAccess] = useState<Access>({ kind: 'loading' });

  const [roleEmail, setRoleEmail] = useState('');
  const [role, setRole] = useState<LogsRole>('viewer');
  const [roleMsg, setRoleMsg] = useState('');
  const [roleBusy, setRoleBusy] = useState(false);

  const [groups, setGroups] = useState<LogGroupConfig[]>([]);
  const [backfillHours, setBackfillHours] = useState(24);
  const [configMsg, setConfigMsg] = useState('');
  const [configBusy, setConfigBusy] = useState(false);

  const [syncStates, setSyncStates] = useState<LogsSyncState[]>([]);
  const [syncMsg, setSyncMsg] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);

  const [audit, setAudit] = useState<LogsAccessAudit[]>([]);
  const [auditBusy, setAuditBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u: any = await getCurrentUser();
        if (!u) return !cancelled && setAccess({ kind: 'denied' });
        const data = await readUser(u.uid);
        const r = resolveLogsRole(data && data.restricted);
        if (!cancelled) setAccess({ kind: r === 'admin' ? 'ok' : 'denied' });
      } catch {
        if (!cancelled) setAccess({ kind: 'denied' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const cfg = await loadLogGroupsConfig();
      setGroups(cfg.groups);
      setBackfillHours(cfg.backfillHours);
    } catch {
      /* config may not exist until the first sync seeds it */
    }
    try {
      setSyncStates((await listSyncState()).sort((a, b) => a.key.localeCompare(b.key)));
    } catch {
      /* gated by rules; page is already gated */
    }
    setAuditBusy(true);
    try {
      setAudit(await listRecentLogsAudit(100));
    } catch {
      /* gated by rules */
    } finally {
      setAuditBusy(false);
    }
  }, []);

  useEffect(() => {
    if (access.kind === 'ok') loadAll();
  }, [access.kind, loadAll]);

  async function submitRole(e: React.FormEvent) {
    e.preventDefault();
    setRoleBusy(true);
    setRoleMsg('');
    try {
      const res = await setLogsRole(roleEmail.trim(), role);
      setRoleMsg(`Set ${res.email} → ${res.role}.`);
      setRoleEmail('');
    } catch (err: any) {
      setRoleMsg(err?.message || 'Failed to set role.');
    } finally {
      setRoleBusy(false);
    }
  }

  async function revokeRole() {
    if (!roleEmail.trim()) return;
    setRoleBusy(true);
    setRoleMsg('');
    try {
      const res = await setLogsRole(roleEmail.trim(), null);
      setRoleMsg(`Revoked access for ${res.email}.`);
      setRoleEmail('');
    } catch (err: any) {
      setRoleMsg(err?.message || 'Failed to revoke.');
    } finally {
      setRoleBusy(false);
    }
  }

  function updateGroup(i: number, patch: Partial<LogGroupConfig>) {
    setGroups((prev) => prev.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  }

  async function saveConfig() {
    setConfigBusy(true);
    setConfigMsg('');
    try {
      const cleaned = groups
        .map((g) => ({ key: g.key.trim(), label: g.label.trim() || g.key.trim(), logGroup: g.logGroup.trim() }))
        .filter((g) => g.key && g.logGroup);
      await saveLogGroupsConfig(cleaned, backfillHours);
      setGroups(cleaned);
      setConfigMsg('Saved. The next sync (≤5 min, or “Sync now”) picks this up.');
    } catch (err: any) {
      setConfigMsg(err?.message || 'Failed to save config.');
    } finally {
      setConfigBusy(false);
    }
  }

  async function runSyncNow() {
    setSyncBusy(true);
    setSyncMsg('');
    try {
      const res = await syncIntakeLogsNow();
      const parts = res.results.map((r) =>
        r.error ? `${r.key}: ERROR ${r.error}` : `${r.key}: ${r.invocations} invocations from ${r.scanned} events`,
      );
      setSyncMsg(parts.join(' · '));
      setSyncStates((await listSyncState()).sort((a, b) => a.key.localeCompare(b.key)));
    } catch (err: any) {
      setSyncMsg(err?.message || 'Sync failed.');
    } finally {
      setSyncBusy(false);
    }
  }

  if (access.kind === 'loading') {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page" style={{ padding: '40px 28px' }}>
              <IonSpinner />
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }
  if (access.kind === 'denied') {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page" style={{ maxWidth: 640, padding: '40px 28px' }}>
              <div className="pm-header">
                <div className="pm-header-titles">
                  <h1>Logs admin</h1>
                  <p>Admins only.</p>
                </div>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const card: React.CSSProperties = {
    background: 'var(--rr-paper, #fff)',
    border: '1px solid var(--pm-border, #d8d4cc)',
    borderRadius: 'var(--pm-radius-lg, 12px)',
    padding: '16px 18px',
    marginTop: 16,
  };
  const input: React.CSSProperties = {
    padding: '8px 10px',
    border: '1px solid var(--pm-border, #d8d4cc)',
    borderRadius: 8,
    fontSize: 14,
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page" style={{ maxWidth: 1040, padding: '24px 28px' }}>
            <div
              className="pm-header"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}
            >
              <div className="pm-header-titles">
                <h1>Logs admin</h1>
                <p>Grant access, configure log groups, watch sync health, review raw-access audits.</p>
              </div>
              <button
                type="button"
                className="pm-btn"
                style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                onClick={() => history.push('/Operator/Logs')}
              >
                ← Intake logs
              </button>
            </div>

            {/* Set / revoke role */}
            <form onSubmit={submitRole} style={card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 16 }}>Set a user’s access</h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  style={{ ...input, flex: '1 1 260px' }}
                  placeholder="email@rolloverrep.com"
                  value={roleEmail}
                  onChange={(e) => setRoleEmail(e.target.value)}
                />
                <select style={input} value={role} onChange={(e) => setRole(e.target.value as LogsRole)}>
                  {LOGS_ROLE_ORDER.map((r) => (
                    <option key={r} value={r}>
                      {LOGS_ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
                <button type="submit" className="pm-btn pm-btn-primary" style={{ padding: '8px 16px' }} disabled={roleBusy}>
                  {roleBusy ? '…' : 'Set role'}
                </button>
                <button type="button" className="pm-btn" style={{ padding: '8px 16px' }} disabled={roleBusy} onClick={revokeRole}>
                  Revoke
                </button>
              </div>
              {roleMsg && <div style={{ marginTop: 8, fontSize: 13 }}>{roleMsg}</div>}
              <p style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                Viewer = dashboard + raw drill-down (audited). Admin = viewer + this page. The account must already exist.
              </p>
            </form>

            {/* Log-group config */}
            <div style={card}>
              <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>CloudWatch log groups</h2>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#888' }}>
                The deployed Lambda names set the log-group names (<code>/aws/lambda/&lt;function name&gt;</code>). If a
                group shows “not found” in sync health below, correct its name here — no redeploy needed.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', background: 'var(--rr-stone-100, #f4f2ee)' }}>
                      <th style={thTd}>Key</th>
                      <th style={thTd}>Label</th>
                      <th style={thTd}>Log group</th>
                      <th style={thTd} />
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--pm-border, #eee)' }}>
                        <td style={thTd}>
                          <input style={{ ...input, width: 130, fontFamily: 'monospace' }} value={g.key} onChange={(e) => updateGroup(i, { key: e.target.value })} />
                        </td>
                        <td style={thTd}>
                          <input style={{ ...input, width: 180 }} value={g.label} onChange={(e) => updateGroup(i, { label: e.target.value })} />
                        </td>
                        <td style={thTd}>
                          <input
                            style={{ ...input, width: '100%', minWidth: 280, fontFamily: 'monospace' }}
                            value={g.logGroup}
                            onChange={(e) => updateGroup(i, { logGroup: e.target.value })}
                          />
                        </td>
                        <td style={thTd}>
                          <button type="button" className="pm-btn" style={{ padding: '6px 10px' }} onClick={() => setGroups((prev) => prev.filter((_, j) => j !== i))}>
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                    {groups.length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ padding: 18, textAlign: 'center', color: '#888' }}>
                          No groups configured yet — the first sync seeds defaults, or add rows here.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="pm-btn"
                  style={{ padding: '8px 16px' }}
                  onClick={() => setGroups((prev) => [...prev, { key: '', label: '', logGroup: '/aws/lambda/' }])}
                >
                  + Add group
                </button>
                <label style={{ fontSize: 13, marginLeft: 'auto' }}>
                  First-sync backfill (hours):{' '}
                  <input
                    style={{ ...input, width: 80 }}
                    type="number"
                    min={1}
                    max={336}
                    value={backfillHours}
                    onChange={(e) => setBackfillHours(Math.max(1, Number(e.target.value) || 24))}
                  />
                </label>
                <button type="button" className="pm-btn pm-btn-primary" style={{ padding: '8px 16px' }} onClick={saveConfig} disabled={configBusy}>
                  {configBusy ? '…' : 'Save config'}
                </button>
              </div>
              {configMsg && <div style={{ marginTop: 8, fontSize: 13 }}>{configMsg}</div>}
            </div>

            {/* Sync health */}
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}>
                <h2 style={{ margin: 0, fontSize: 16 }}>Sync health</h2>
                <button className="pm-btn pm-btn-primary" style={{ padding: '6px 12px' }} onClick={runSyncNow} disabled={syncBusy}>
                  {syncBusy ? 'Syncing…' : 'Sync now'}
                </button>
              </div>
              {syncMsg && <div style={{ padding: '0 18px 10px', fontSize: 13 }}>{syncMsg}</div>}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', background: 'var(--rr-stone-100, #f4f2ee)' }}>
                      <th style={thTd}>Group</th>
                      <th style={thTd}>Log group</th>
                      <th style={thTd}>Last run</th>
                      <th style={thTd}>Last run: events → invocations</th>
                      <th style={thTd}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncStates.map((s) => (
                      <tr key={s.key} style={{ borderTop: '1px solid var(--pm-border, #eee)' }}>
                        <td style={thTd}>{s.key}</td>
                        <td style={{ ...thTd, fontFamily: 'monospace' }}>{s.log_group}</td>
                        <td style={thTd}>{fmtWhen(s.last_run_at)}</td>
                        <td style={thTd}>
                          {s.last_scanned == null ? '—' : `${s.last_scanned} → ${s.last_invocations ?? 0}`}
                          {s.truncated ? ' (truncated — catching up)' : ''}
                        </td>
                        <td style={{ ...thTd, whiteSpace: 'normal', maxWidth: 320, color: s.last_error ? 'var(--rr-red, #c62828)' : 'var(--rr-green, #2e7d32)' }}>
                          {s.last_error || 'OK'}
                        </td>
                      </tr>
                    ))}
                    {syncStates.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                          No sync has run yet. Deploy the functions, then “Sync now.”
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Audit log */}
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}>
                <h2 style={{ margin: 0, fontSize: 16 }}>Raw-log access audit</h2>
                <button className="pm-btn" style={{ padding: '6px 12px' }} onClick={loadAll} disabled={auditBusy}>
                  {auditBusy ? '…' : 'Refresh'}
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', background: 'var(--rr-stone-100, #f4f2ee)' }}>
                      <th style={thTd}>When</th>
                      <th style={thTd}>Who</th>
                      <th style={thTd}>Client</th>
                      <th style={thTd}>Session</th>
                      <th style={thTd}>Lambda</th>
                      <th style={thTd}>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((a) => (
                      <tr key={a.id} style={{ borderTop: '1px solid var(--pm-border, #eee)' }}>
                        <td style={thTd}>{fmtWhen(a.created_at)}</td>
                        <td style={thTd}>{a.email}</td>
                        <td style={thTd}>{a.center || '—'}</td>
                        <td style={{ ...thTd, fontFamily: 'monospace' }}>{a.session_id || '—'}</td>
                        <td style={thTd}>{a.lambda || '—'}</td>
                        <td style={thTd}>{a.ip || '—'}</td>
                      </tr>
                    ))}
                    {audit.length === 0 && !auditBusy && (
                      <tr>
                        <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                          No audit entries yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

const thTd: React.CSSProperties = { padding: '9px 12px', whiteSpace: 'nowrap' };

export default LogsAdmin;
